import {
  ConstructGeneralViewStateParam,
  TradingAssistantRestoreLocalSavedViewProps,
  TradingAssistantViewState,
} from '@models/SavedViews';
import { useRestoreSavedView } from '@ecosystem/newron-client-lib';
import {
  constructGeneralViewStateProps,
  handleCustomPeriodicityAndRestoreGeneralViewState,
  isMultibannerChanged,
} from '@utils/hook/saveViews/SavedViewHookUtils';
import { useDispatch, useSelector } from 'react-redux';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { bindActionCreators } from 'redux';
import { actionCreators, actionCreatorsSideFilters } from '@state/Index';
import {
  RestoreMultibannerRetailerViewProps,
  useRestoreMultibannerRetailerClient,
} from '@utils/hook/saveViews/UseRestoreMultibannerRetailerClient';
import { useRestoreProductMultibannerRetailerClient } from '@utils/hook/saveViews/UseRestoreProductMultibannerRetailerClient';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { useUpdatePerfPeriodSet } from '@utils/period-set/UseUpdatePerfPeriodSet';

/**
 *  React custom hooks to restore the saved views for Trading Assistant page
 *  @param tradingAssistantRestoreLocalSavedViewProps contains local setState from TA page
 */
export const useRestoreTradingAssistantSavedView = (
  tradingAssistantRestoreLocalSavedViewProps: TradingAssistantRestoreLocalSavedViewProps,
): void => {
  const {
    setAppliedCountries,
    setAppliedProductGroupOptions,
    setBrandFilterOptions,
    setDefaultFilters,
    setFeature,
    setFilters,
    setGrowthOption,
    setPgsByAppliedCountries,
    setPeriod,
    setRelativePeriod,
    setSelectedCountries,
    setSelectedProductGroupOptions,
    setTimeFrame,
    setWeeklyMonthlyFeatures,
    setViewingBrand,
    setKpi,
    setSortBy,
    setCustomWeeksPeriodState,
    setCustomMonthsPeriodState,
    setIsDeepDiveView,
  } = tradingAssistantRestoreLocalSavedViewProps;

  // generate object holding the general states to be passed in the function later on
  const generalSavedViewProps = constructGeneralViewStateProps(tradingAssistantRestoreLocalSavedViewProps);

  const dispatch = useDispatch();
  const pagesConfig = usePageConfig();

  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);

  const { restoreMultibannerRetailerClient } = useRestoreMultibannerRetailerClient();
  const { restoreProductMultibannerRetailerClient } = useRestoreProductMultibannerRetailerClient();

  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const mainFiltersState = useSelector((state: any) => state.mainFilters);
  const { updatePeriod, updatePeriodicity, updateComparisonPeriod } = useUpdatePerfPeriodSet();
  const pageName = PageName.TRADING_ASSISTANT;
  const pageType = PageType.TRADING_ASSISTANT;

  const applyTAViewState = (viewState: TradingAssistantViewState): void => {
    if (!viewState) {
      return;
    }

    const params: ConstructGeneralViewStateParam = {
      viewState,
      pagesConfig,
      pageType,
      pageName,
      updateSideFilters,
      updateMainFilters,
      mainFiltersState,
      generalSavedViewProps,
    };

    // restore the general states that exist in all pages
    handleCustomPeriodicityAndRestoreGeneralViewState(
      viewState,
      params,
      setCustomMonthsPeriodState,
      setCustomWeeksPeriodState,
    );

    // below are states that are specific to TA page

    if (viewState.selectedCountries) {
      setSelectedCountries(viewState.selectedCountries);
    }

    if (viewState.appliedCountries) {
      setAppliedCountries(viewState.appliedCountries);
    }

    if (viewState.selectedProductGroup) {
      setSelectedProductGroupOptions(viewState.selectedProductGroup);
    }

    if (viewState.appliedProductGroup) {
      setAppliedProductGroupOptions(viewState.appliedProductGroup);
    }

    if (viewState.brandFilterOptions) {
      setBrandFilterOptions(viewState.brandFilterOptions);
    }

    if (viewState.pgsByAppliedCountries) {
      setPgsByAppliedCountries(viewState.pgsByAppliedCountries);
    }

    if (viewState.mainFilter) {
      //Updating local state in to be sync with redux state
      setRelativePeriod(viewState.mainFilter.comparisonPeriod);
      setTimeFrame(viewState.mainFilter.timeFrame);
      setPeriod(viewState.mainFilter.period);
      //Updating mainfiltermarketPremium Redux states
      updatePeriodicity(viewState.mainFilter.timeFrame);
      updatePeriod(viewState.mainFilter.period);
      updateComparisonPeriod(viewState.mainFilter.comparisonPeriod);
    }

    if (viewState.feature) {
      setFeature(viewState.feature);
    }

    if (viewState.weeklyMonthlyFeatures) {
      setWeeklyMonthlyFeatures(viewState.weeklyMonthlyFeatures);
    }

    if (viewState.growthType) {
      setGrowthOption(viewState.growthType as GrowthTypes);
    }

    if (viewState.viewingBrand) {
      setViewingBrand(viewState.viewingBrand, 'restore');
    }

    if (viewState.kpi) {
      setKpi(viewState.kpi);
    }
    if (viewState.sortBy) {
      setSortBy(viewState.sortBy);
    }
    if (viewState.isDeepDiveView) {
      setIsDeepDiveView(viewState.isDeepDiveView);
    }
    const {
      retailerClientState: viewRetailerClientState,
      initialFilterState,
      appliedCountries,
      hitlistRetailerClientState,
      hitlistInitialFilterState,
      productMainFilterState,
    } = viewState;

    const restoreMultibannerProps: RestoreMultibannerRetailerViewProps = {
      pagesConfig,
      retailerClientState: viewRetailerClientState,
      initialFilterState,
      appliedCountry: appliedCountries,
      pageName,
      pageType,
      setFilters,
      setAppliedCountries,
      setSelectedCountries,
      setDefaultFilters,
    };

    const restoreProductMultibannerProps = {
      pagesConfig,
      hitlistRetailerClientState,
      hitllistInitialFilterState: hitlistInitialFilterState,
      productMainFilterState,
      appliedCountry: appliedCountries,
      pageName,
    };

    isMultibannerChanged(
      viewRetailerClientState.clientId,
      retailerClientState.clientId,
      restoreMultibannerProps,
      restoreProductMultibannerProps,
      restoreMultibannerRetailerClient,
      restoreProductMultibannerRetailerClient,
    );
  };
  useRestoreSavedView(applyTAViewState);
};
